import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  TextField,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  changeMachine,
  getMachines,
  getManufacturers,
  getOperationTypes,
  getAxisConfigurations,
  getControlSystemBrands,
  getControlSystemModels,
  getAutomationLevels,
  setSuccess,
} from "../customerMachinerySlice";
import {
  Machine,
  MachineTypes,
  MachineFileItem,
  ChangeMachine as ChangeMachineModel,
  CustomerMachineryState,
  CatalogModelListItem,
} from "../models/models";
import CatalogModelSearch from "./CatalogModelSearch";
import { dt } from "../utils/dt";
import { accentGradientLight, accentGradientLightHover, accentShimmer } from "../../../shared/themes/redesignTokens";
import { getMachineDisplayName } from "../../../shared/utils/machineUtils";
import useTranslation from "../../../shared/hooks/useTranslation";
import { MAX_FILE_SIZE_MB } from "../../../shared/models/models";
import { MultiImageUpload } from "../../../shared/components/gallery";
import type { GalleryImage } from "../../../shared/components/gallery";
import LocationPicker from "./locations/LocationPicker";
import { fetchLocations } from "../customerLocationsSlice";

// =====================================================================
//  PROPS
// =====================================================================

interface MachineEditDialogProps {
  open: boolean;
  machine: Machine | null;
  onClose: () => void;
  onSaved: () => void;
}

// =====================================================================
//  FIELD LABEL
// =====================================================================

const FieldLabel = ({ children, required, icon }: { children: React.ReactNode; required?: boolean; icon?: React.ReactNode }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      mb: "6px",
    }}
  >
    {icon}
    <Box
      sx={{
        fontSize: "12px",
        fontWeight: 600,
        fontFamily: dt.font,
        color: dt.textSecondary,
        textTransform: "uppercase",
        letterSpacing: "0.8px",
      }}
    >
      {children}
      {required && <span style={{ color: "#E74C3C", marginLeft: "2px" }}>*</span>}
    </Box>
  </Box>
);

// =====================================================================
//  SECTION DIVIDER
// =====================================================================

const SectionDivider = () => (
  <Box
    sx={{
      borderTop: `1px solid ${dt.cardBorder}`,
      my: "20px",
    }}
  />
);

// =====================================================================
//  COMPONENT
// =====================================================================

const MachineEditDialog = ({ open, machine, onClose, onSaved }: MachineEditDialogProps) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");
  const isPhone = useMediaQuery("(max-width:600px)");

  const {
    query,
    types,
    manufacturers,
    operationTypes,
    axisConfigurations,
    controlSystemBrands,
    controlSystemModels,
    automationLevels,
  } = useAppSelector<CustomerMachineryState>((state: any) => state.customerMachinery);

  // =====================================================================
  //  LOCAL FORM STATE
  // =====================================================================

  const [serialNumber, setSerialNumber] = useState<string>("");
  const [productionYear, setProductionYear] = useState<string>("");
  const [typeId, setTypeId] = useState<string>("");
  const [manufacturerId, setManufacturerId] = useState<string>("");
  const [customBrandName, setCustomBrandName] = useState<string>("");
  const [useCustomBrand, setUseCustomBrand] = useState(false);
  const [operationTypeId, setOperationTypeId] = useState<string>("");
  const [axisConfigurationId, setAxisConfigurationId] = useState<string>("");
  const [controlSystemBrandId, setControlSystemBrandId] = useState<string>("");
  const [system, setSystem] = useState<string>("");
  const [automationLevelId, setAutomationLevelId] = useState<string>("");
  const [numberOfStations, setNumberOfStations] = useState<string>("");
  const [locationId, setLocationId] = useState<string | null>(null);

  // Multi-image gallery state
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [initialGalleryCount, setInitialGalleryCount] = useState(0);

  // Catalog model state
  const [selectedCatalogModel, setSelectedCatalogModel] = useState<CatalogModelListItem | null>(null);
  const [catalogModelId, setCatalogModelId] = useState<string | undefined>(undefined);

  // UI state
  const [saving, setSaving] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [refDataLoading, setRefDataLoading] = useState(false);

  // Track initial values for dirty check
  const [initialValues, setInitialValues] = useState<Record<string, any>>({});

  // Location state — wired to Redux
  const locations = useAppSelector((state) => state.customerLocations.locations);

  // Fetch locations when dialog opens (not on mount — avoids 403 for roles without PartnerHub)
  useEffect(() => {
    if (open && locations.length === 0) {
      dispatch(fetchLocations());
    }
  }, [dispatch, open, locations.length]);

  // Ref to the current machine — used in post-load useEffects to avoid
  // stale closures or re-triggering when machine object reference changes.
  const machineRef = useRef<Machine | null>(null);
  machineRef.current = machine;

  // =====================================================================
  //  INJECT DIALOG KEYFRAMES
  // =====================================================================

  useEffect(() => {
    const styleId = "machine-edit-dialog-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes dialogSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // =====================================================================
  //  POPULATE FORM FROM MACHINE (on open / machine change)
  // =====================================================================

  useEffect(() => {
    if (machine && open) {
      const sn = machine.serialNumber || "";
      const py = machine.productionYear ? String(machine.productionYear) : "";
      const tId = machine.type?.id || "";
      const mId = machine.manufacturer?.id || "";
      const cBrand = machine.customBrandName || "";
      const oId = machine.operationType?.id || "";
      const aId = machine.axisConfiguration?.id || "";
      const csBId = machine.controlSystemBrand?.id || "";
      const sys = machine.system || "";
      const alId = machine.automationLevel?.id || "";
      const nos = machine.numberOfStations ? String(machine.numberOfStations) : "";

      setSerialNumber(sn);
      setProductionYear(py);
      setTypeId(tId);
      setManufacturerId(mId);
      setCustomBrandName(cBrand);
      setUseCustomBrand(!!cBrand && cBrand.length > 0);
      setOperationTypeId(oId);
      setAxisConfigurationId(aId);
      setControlSystemBrandId(csBId);
      setSystem(sys);
      setAutomationLevelId(alId);
      setNumberOfStations(nos);

      // Multi-image: load from machine.files, or fall back to legacy single image
      const initialImages: GalleryImage[] = [];
      if (machine.files && machine.files.length > 0) {
        [...machine.files]
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .forEach((f, idx) => {
            initialImages.push({
              id: f.id || `existing-${idx}`,
              src: f.content,
              name: f.name,
              size: f.size,
              isPrimary: f.isPrimary,
            });
          });
      } else if (machine.image?.content) {
        initialImages.push({
          id: "legacy-image",
          src: machine.image.content,
          name: machine.image.name || "machine-image",
          isPrimary: true,
        });
      }
      setGalleryImages(initialImages);
      setInitialGalleryCount(initialImages.length);

      // Catalog model
      if (machine.catalogModel) {
        setSelectedCatalogModel(machine.catalogModel);
        setCatalogModelId(machine.catalogModelId);
      } else {
        setSelectedCatalogModel(null);
        setCatalogModelId(machine.catalogModelId);
      }

      const locId = (machine as any).locationId || null;
      setLocationId(locId);

      setInitialValues({
        serialNumber: sn,
        productionYear: py,
        typeId: tId,
        manufacturerId: mId,
        customBrandName: cBrand,
        operationTypeId: oId,
        axisConfigurationId: aId,
        controlSystemBrandId: csBId,
        system: sys,
        automationLevelId: alId,
        numberOfStations: nos,
        catalogModelId: machine.catalogModelId,
        locationId: locId,
      });
    }
  }, [machine, open]);

  // =====================================================================
  //  CASCADING DATA LOADS
  // =====================================================================

  useEffect(() => {
    if (typeId && open) {
      setRefDataLoading(true);
      Promise.all([
        dispatch(getManufacturers(typeId)),
        dispatch(getOperationTypes(typeId)),
        dispatch(getControlSystemBrands(typeId)),
        dispatch(getAutomationLevels()),
      ]).finally(() => setRefDataLoading(false));
    }
  }, [typeId, open, dispatch]);

  useEffect(() => {
    if (operationTypeId && open) {
      dispatch(getAxisConfigurations(operationTypeId));
    }
  }, [operationTypeId, open, dispatch]);

  useEffect(() => {
    if (controlSystemBrandId && open) {
      dispatch(getControlSystemModels(controlSystemBrandId));
    }
  }, [controlSystemBrandId, open, dispatch]);

  // =====================================================================
  //  RE-APPLY MACHINE VALUES AFTER REF DATA LOADS
  //  These effects ensure that when async reference data finishes loading,
  //  the dropdown values are set to the machine's existing values.
  // =====================================================================

  // After operation types load, re-apply machine's operation type
  useEffect(() => {
    const m = machineRef.current;
    if (!open || !m || operationTypes.length === 0) return;
    const machineOpId = m.operationType?.id || "";
    if (machineOpId && operationTypes.some((ot) => ot.id === machineOpId)) {
      setOperationTypeId((prev) => {
        // Only set if it's still the initial value or empty
        if (!prev || prev === machineOpId) return machineOpId;
        return prev;
      });
    }
  }, [operationTypes, open]);

  // After automation levels load, re-apply machine's automation level
  useEffect(() => {
    const m = machineRef.current;
    if (!open || !m || automationLevels.length === 0) return;
    const machineAlId = m.automationLevel?.id || "";
    if (machineAlId && automationLevels.some((al) => al.id === machineAlId)) {
      setAutomationLevelId((prev) => {
        if (!prev || prev === machineAlId) return machineAlId;
        return prev;
      });
    }
  }, [automationLevels, open]);

  // After control system brands load, re-apply machine's control system brand
  useEffect(() => {
    const m = machineRef.current;
    if (!open || !m || controlSystemBrands.length === 0) return;
    const machineCsBId = m.controlSystemBrand?.id || "";
    if (machineCsBId && controlSystemBrands.some((csb) => csb.id === machineCsBId)) {
      setControlSystemBrandId((prev) => {
        if (!prev || prev === machineCsBId) return machineCsBId;
        return prev;
      });
    }
  }, [controlSystemBrands, open]);

  // After axis configurations load, re-apply machine's axis configuration
  useEffect(() => {
    const m = machineRef.current;
    if (!open || !m || axisConfigurations.length === 0) return;
    const machineAxisId = m.axisConfiguration?.id || "";
    if (machineAxisId && axisConfigurations.some((ac) => ac.id === machineAxisId)) {
      setAxisConfigurationId((prev) => {
        if (!prev || prev === machineAxisId) return machineAxisId;
        return prev;
      });
    }
  }, [axisConfigurations, open]);

  // After manufacturers load, re-apply machine's manufacturer
  useEffect(() => {
    const m = machineRef.current;
    if (!open || !m || manufacturers.length === 0) return;
    const machineMfgId = m.manufacturer?.id || "";
    if (machineMfgId && manufacturers.some((mfg) => mfg.id === machineMfgId)) {
      setManufacturerId((prev) => {
        if (!prev || prev === machineMfgId) return machineMfgId;
        return prev;
      });
    }
  }, [manufacturers, open]);

  // =====================================================================
  //  DIRTY CHECK
  // =====================================================================

  // Detect gallery changes: count mismatch or any new images
  const galleryChanged = useMemo(() => {
    if (galleryImages.length !== initialGalleryCount) return true;
    return galleryImages.some((img) => img.isNew === true);
  }, [galleryImages, initialGalleryCount]);

  const isDirty = useMemo(() => {
    return (
      serialNumber !== initialValues.serialNumber ||
      productionYear !== initialValues.productionYear ||
      typeId !== initialValues.typeId ||
      manufacturerId !== initialValues.manufacturerId ||
      customBrandName !== initialValues.customBrandName ||
      operationTypeId !== initialValues.operationTypeId ||
      axisConfigurationId !== initialValues.axisConfigurationId ||
      controlSystemBrandId !== initialValues.controlSystemBrandId ||
      system !== initialValues.system ||
      automationLevelId !== initialValues.automationLevelId ||
      numberOfStations !== initialValues.numberOfStations ||
      catalogModelId !== initialValues.catalogModelId ||
      locationId !== initialValues.locationId ||
      galleryChanged
    );
  }, [
    serialNumber, productionYear, typeId, manufacturerId, customBrandName,
    operationTypeId, axisConfigurationId, controlSystemBrandId, system,
    automationLevelId, numberOfStations, catalogModelId, locationId,
    galleryChanged, initialValues,
  ]);

  // =====================================================================
  //  CLOSE HANDLING (with dirty check)
  // =====================================================================

  const handleAttemptClose = useCallback(() => {
    if (isDirty) {
      setDiscardDialogOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const handleDiscardConfirm = useCallback(() => {
    setDiscardDialogOpen(false);
    onClose();
  }, [onClose]);

  // =====================================================================
  //  GALLERY CHANGE HANDLER
  // =====================================================================

  const handleGalleryChange = useCallback((images: GalleryImage[]) => {
    setGalleryImages(images);
  }, []);

  // =====================================================================
  //  CATALOG MODEL HANDLERS
  // =====================================================================

  const handleCatalogModelSelect = useCallback((model: CatalogModelListItem) => {
    setSelectedCatalogModel(model);
    setCatalogModelId(model.id);

    // Auto-fill manufacturer if catalog provides mapping
    if (model.manufacturer?.manufacturerId) {
      setManufacturerId(model.manufacturer.manufacturerId);
    }
  }, []);

  const handleClearCatalogModel = useCallback(() => {
    setSelectedCatalogModel(null);
    setCatalogModelId(undefined);
  }, []);

  // =====================================================================
  //  TYPE CHANGE -- reset downstream values
  // =====================================================================

  const handleTypeChange = useCallback((newTypeId: string) => {
    setTypeId(newTypeId);
    // Reset downstream when type changes
    if (newTypeId !== typeId) {
      setManufacturerId("");
      setOperationTypeId("");
      setAxisConfigurationId("");
      setControlSystemBrandId("");
      setSystem("");
      setAutomationLevelId("");
    }
  }, [typeId]);

  // =====================================================================
  //  SAVE
  // =====================================================================

  const handleSave = useCallback(async () => {
    if (!machine) return;
    setSaving(true);

    try {
      // Convert gallery images to MachineFileItem[] for the API
      const files: MachineFileItem[] = galleryImages.map((img, idx) => ({
        id: img.isNew ? undefined : img.id,
        name: img.name || `image-${idx + 1}`,
        content: img.src,
        displayOrder: idx,
        isPrimary: img.isPrimary === true,
        size: img.size,
      }));

      const model: ChangeMachineModel = {
        model: machine.model,
        serialNumber: serialNumber,
        typeId: typeId || machine.type?.id,
        manufacturerId: manufacturerId || machine.manufacturer?.id,
        customBrandName: useCustomBrand ? customBrandName : undefined,
        operationTypeId: operationTypeId || machine.operationType?.id,
        axisConfigurationId: axisConfigurationId || machine.axisConfiguration?.id,
        controlSystemBrandId: controlSystemBrandId || machine.controlSystemBrand?.id,
        automationLevelId: automationLevelId || machine.automationLevel?.id,
        system: system || undefined,
        version: machine.version || undefined,
        productionYear: productionYear ? parseInt(productionYear, 10) : undefined,
        description: machine.description || undefined,
        numberOfStations: numberOfStations ? parseInt(numberOfStations, 10) : undefined,
        catalogModelId: catalogModelId,
        locationId: locationId || undefined,
        image: null, // Legacy field — using files[] instead
        files: files.length > 0 ? files : undefined,
      };

      await dispatch(changeMachine({ id: machine.id, model }));
      dispatch(setSuccess(translate("panelSaved")));
      await dispatch(getMachines(query));
      onSaved();
    } catch (err) {
      console.error("Failed to save machine edit dialog:", err);
    } finally {
      setSaving(false);
    }
  }, [
    machine, serialNumber, productionYear, typeId, manufacturerId,
    customBrandName, useCustomBrand, operationTypeId, axisConfigurationId,
    controlSystemBrandId, system, automationLevelId, numberOfStations, locationId,
    catalogModelId, galleryImages, dispatch, query, onSaved, translate,
  ]);

  // =====================================================================
  //  DERIVED STATE
  // =====================================================================

  const isProductionLine = useMemo(() => {
    if (!typeId || !types.length) return false;
    const selectedType = types.find((t) => t.id === typeId);
    return selectedType?.name === MachineTypes.productionLine;
  }, [typeId, types]);

  const isCncMachine = useMemo(() => {
    if (!typeId || !types.length) return false;
    const selectedType = types.find((t) => t.id === typeId);
    return selectedType?.name === MachineTypes.cnc;
  }, [typeId, types]);

  const displayName = machine ? getMachineDisplayName(machine) : "";

  // =====================================================================
  //  SKELETON LOADER FOR DROPDOWNS
  // =====================================================================

  const DropdownSkeleton = () => (
    <Skeleton
      variant="rounded"
      height={42}
      sx={{ borderRadius: dt.radiusSm }}
    />
  );

  // =====================================================================
  //  SELECT SX
  // =====================================================================

  const selectSx = {
    borderRadius: dt.radiusSm,
    fontSize: "14px",
    fontFamily: dt.font,
    "& .MuiSelect-select": { padding: "10px 14px", fontFamily: dt.font },
  };

  // =====================================================================
  //  RENDER
  // =====================================================================

  if (!machine) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleAttemptClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhone}
        PaperProps={{
          sx: {
            borderRadius: isPhone ? 0 : dt.radiusLg,
            overflow: "hidden",
            fontFamily: dt.font,
            "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
            animation: isPhone ? undefined : "dialogSlideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
            maxHeight: isPhone ? "100vh" : "90vh",
          },
        }}
      >
        {/* -- Blue gradient accent bar -- */}
        <Box
          sx={{
            height: "4px",
            background: accentShimmer,
          }}
        />

        {/* -- Header -- */}
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 0,
            pt: 2,
            px: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: dt.blueSoft,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PrecisionManufacturingOutlinedIcon
                sx={{ color: dt.blue, fontSize: 20 }}
              />
            </Box>
            <Box>
              <Box
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "17px", sm: "19px" },
                  color: dt.textPrimary,
                  letterSpacing: "-0.3px",
                }}
              >
                {translate("editPanelTitle")}
              </Box>
              <Box
                sx={{
                  fontSize: "13px",
                  color: dt.textSecondary,
                  mt: "-1px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: isPhone ? "200px" : "350px",
                }}
              >
                {displayName}
              </Box>
            </Box>
          </Box>
          <IconButton
            onClick={handleAttemptClose}
            sx={{
              color: dt.textSecondary,
              "&:hover": { color: dt.textPrimary, backgroundColor: dt.inputBg },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* -- Content -- */}
        <DialogContent
          sx={{
            px: 3,
            pt: 2,
            pb: 0,
            "&::-webkit-scrollbar": { width: "5px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(0,0,0,0.12)",
              borderRadius: "3px",
              "&:hover": { background: "rgba(0,0,0,0.2)" },
            },
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(0,0,0,0.12) transparent",
          }}
        >
          {/* -- Image Section (Multi-Image Upload) -- */}
          <Box sx={{ mb: "20px" }}>
            <MultiImageUpload
              images={galleryImages}
              onChange={handleGalleryChange}
              maxImages={10}
              maxFileSizeMb={MAX_FILE_SIZE_MB}
              compact
            />
          </Box>

          {/* =========================================================
              FIELD ORDER (by dependency chain):
              1. Catalog Model
              2. Machine Type
              3. Manufacturer / Custom Brand
              4. Operation Type
              5. Axis Configuration (CNC only)
              6. Control System Brand
              7. Control System Model
              8. Automation Level
              9. Number of Stations (Production Line only)
              10. Serial Number
              11. Production Year
              ========================================================= */}

          {/* -- 1. Catalog Model (first after image) -- */}
          <FieldLabel>{translate("catalogModel")}</FieldLabel>
          {machine.catalogModel && !selectedCatalogModel ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                background: dt.blueSoft,
                border: `1px solid ${dt.blueGlow}`,
                borderRadius: dt.radiusSm,
                padding: "8px 12px",
              }}
            >
              <PrecisionManufacturingOutlinedIcon sx={{ color: dt.blue, fontSize: 16 }} />
              <Box sx={{ fontSize: "12px", color: dt.textSecondary, fontFamily: dt.font }}>
                <strong style={{ color: dt.textPrimary }}>{machine.catalogModel.modelName}</strong>
              </Box>
            </Box>
          ) : (
            <CatalogModelSearch
              onSelect={handleCatalogModelSelect}
              selectedModel={selectedCatalogModel}
              onClear={handleClearCatalogModel}
            />
          )}

          <SectionDivider />

          {/* -- 2. Machine Type -- */}
          <FieldLabel>{translate("machineType")}</FieldLabel>
          {types.length === 0 ? (
            <DropdownSkeleton />
          ) : (
            <FormControl fullWidth size="small">
              <Select
                value={typeId}
                onChange={(e) => handleTypeChange(e.target.value as string)}
                displayEmpty
                sx={selectSx}
                renderValue={(selected) => {
                  if (!selected) return <Box sx={{ color: dt.textMuted }}>{translate("chooseFromList")}</Box>;
                  return types.find((t) => t.id === selected)?.name || "";
                }}
              >
                {types.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <SectionDivider />

          {/* -- Location (between Machine Type and Manufacturer) -- */}
          <FieldLabel icon={<PlaceOutlinedIcon sx={{ fontSize: 14, color: dt.textSecondary }} />}>
            {translate("location") || "Location"}
          </FieldLabel>
          <LocationPicker
            value={locationId}
            onChange={setLocationId}
            locations={locations}
          />

          <SectionDivider />

          {/* -- 3. Manufacturer -- */}
          <FieldLabel>{translate("selectManufacturer")}</FieldLabel>
          {refDataLoading ? (
            <DropdownSkeleton />
          ) : (
            <Autocomplete
              size="small"
              options={manufacturers}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              value={manufacturers.find((m) => m.id === manufacturerId) || null}
              onChange={(_, selected) => setManufacturerId(selected?.id || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={translate("chooseFromList")}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: dt.radiusSm },
                  }}
                />
              )}
            />
          )}

          {/* Custom brand toggle */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "10px" }}>
            <Switch
              size="small"
              checked={useCustomBrand}
              onChange={(e) => setUseCustomBrand(e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: dt.blue },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: dt.blue },
              }}
            />
            <Box sx={{ fontSize: "13px", color: dt.textSecondary, fontFamily: dt.font }}>
              {translate("useCustomBrand")}
            </Box>
          </Box>

          {useCustomBrand && (
            <TextField
              fullWidth
              size="small"
              value={customBrandName}
              onChange={(e) => setCustomBrandName(e.target.value)}
              placeholder={translate("customBrand")}
              inputProps={{ maxLength: 50 }}
              sx={{
                mt: "8px",
                "& .MuiOutlinedInput-root": { borderRadius: dt.radiusSm },
              }}
            />
          )}

          <SectionDivider />

          {/* -- 4. Operation Type -- */}
          <FieldLabel>{translate("operationType")}</FieldLabel>
          {refDataLoading ? (
            <DropdownSkeleton />
          ) : (
            <FormControl fullWidth size="small">
              <Select
                value={operationTypeId}
                onChange={(e) => {
                  setOperationTypeId(e.target.value as string);
                  setAxisConfigurationId("");
                }}
                displayEmpty
                sx={selectSx}
                renderValue={(selected) => {
                  if (!selected) return <Box sx={{ color: dt.textMuted }}>{translate("chooseFromList")}</Box>;
                  return operationTypes.find((x) => x.id === selected)?.name || "";
                }}
              >
                {operationTypes.map((op) => (
                  <MenuItem key={op.id} value={op.id}>
                    {op.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <SectionDivider />

          {/* -- 5. Axis Configuration (only for CNC) -- */}
          {isCncMachine && (
            <>
              <FieldLabel>{translate("axisConfiguration")}</FieldLabel>
              {!operationTypeId || refDataLoading ? (
                <DropdownSkeleton />
              ) : (
                <Autocomplete
                  size="small"
                  options={axisConfigurations}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  value={axisConfigurations.find((a) => a.id === axisConfigurationId) || null}
                  onChange={(_, selected) => setAxisConfigurationId(selected?.id || "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={translate("chooseFromList")}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: dt.radiusSm },
                      }}
                    />
                  )}
                />
              )}
              <SectionDivider />
            </>
          )}

          {/* -- 6. Control System Brand -- */}
          <FieldLabel>{translate("controlSystemBrand")}</FieldLabel>
          {refDataLoading ? (
            <DropdownSkeleton />
          ) : (
            <FormControl fullWidth size="small">
              <Select
                value={controlSystemBrandId}
                onChange={(e) => {
                  setControlSystemBrandId(e.target.value as string);
                  setSystem("");
                }}
                displayEmpty
                sx={selectSx}
                renderValue={(selected) => {
                  if (!selected) return <Box sx={{ color: dt.textMuted }}>{translate("chooseFromList")}</Box>;
                  return controlSystemBrands.find((x) => x.id === selected)?.name || "";
                }}
              >
                {controlSystemBrands.map((csb) => (
                  <MenuItem key={csb.id} value={csb.id}>
                    {csb.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <SectionDivider />

          {/* -- 7. Control System Model (freeSolo) -- */}
          <FieldLabel>{translate("controlSystemModel")}</FieldLabel>
          <Autocomplete
            freeSolo
            size="small"
            disabled={!controlSystemBrandId}
            options={controlSystemModels.map((m) => m.name)}
            getOptionLabel={(option) => option}
            value={system}
            onInputChange={(_, value) => setSystem(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={translate("typeOrChoose")}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: dt.radiusSm },
                }}
              />
            )}
          />

          <SectionDivider />

          {/* -- 8. Automation Level -- */}
          <FieldLabel>{translate("automationLevel")}</FieldLabel>
          {refDataLoading ? (
            <DropdownSkeleton />
          ) : (
            <FormControl fullWidth size="small">
              <Select
                value={automationLevelId}
                onChange={(e) => setAutomationLevelId(e.target.value as string)}
                displayEmpty
                sx={selectSx}
                renderValue={(selected) => {
                  if (!selected) return <Box sx={{ color: dt.textMuted }}>{translate("chooseFromList")}</Box>;
                  return automationLevels.find((x) => x.id === selected)?.name || "";
                }}
              >
                {automationLevels.map((al) => (
                  <MenuItem key={al.id} value={al.id}>
                    {al.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* -- 9. Number of Stations (only for Production Line) -- */}
          {isProductionLine && (
            <>
              <SectionDivider />
              <FieldLabel>{translate("numberOfStations")}</FieldLabel>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={numberOfStations}
                onChange={(e) => setNumberOfStations(e.target.value)}
                inputProps={{ min: 1, max: 9999 }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: dt.radiusSm },
                }}
              />
            </>
          )}

          <SectionDivider />

          {/* -- 10. Serial Number -- */}
          <FieldLabel>{translate("serialNumber")}</FieldLabel>
          <TextField
            fullWidth
            size="small"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            placeholder={translate("serialNumberPlaceholder")}
            inputProps={{ maxLength: 200, style: { fontFamily: dt.fontMono, fontSize: "14px" } }}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: dt.radiusSm },
            }}
          />

          <SectionDivider />

          {/* -- 11. Production Year -- */}
          <FieldLabel>{translate("productionYear")}</FieldLabel>
          <TextField
            fullWidth
            size="small"
            type="number"
            value={productionYear}
            onChange={(e) => setProductionYear(e.target.value)}
            inputProps={{
              min: 1950,
              max: new Date().getFullYear(),
              style: { fontFamily: dt.fontMono, fontSize: "14px" },
            }}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: dt.radiusSm },
            }}
          />

          {/* Bottom spacing inside scroll area */}
          <Box sx={{ height: "16px" }} />
        </DialogContent>

        {/* -- Footer -- */}
        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
            pt: 1.5,
            gap: 1,
            borderTop: `1px solid ${dt.cardBorder}`,
            flexDirection: isPhone ? "column-reverse" : "row",
            "& > :not(:first-of-type)": {
              ml: isPhone ? "0 !important" : undefined,
            },
          }}
        >
          <Button
            onClick={handleAttemptClose}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              color: dt.textSecondary,
              borderRadius: dt.radiusSm,
              px: 2.5,
              py: 1,
              transition: "all 0.2s ease",
              width: isPhone ? "100%" : "auto",
              "&:hover": {
                backgroundColor: dt.inputBg,
                color: dt.textPrimary,
              },
            }}
          >
            {tCommon("buttons.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !isDirty}
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{
              background: accentGradientLight,
              borderRadius: dt.radiusPill,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              px: 3,
              py: 1,
              boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              width: isPhone ? "100%" : "auto",
              whiteSpace: "nowrap",
              "&:hover": {
                background: accentGradientLightHover,
                transform: "translateY(-2px)",
                boxShadow: "0 6px 20px rgba(52,152,219,0.3)",
              },
              "&.Mui-disabled": {
                background: "#D1D5DB",
                color: "#9CA3AF",
                boxShadow: "none",
              },
            }}
          >
            {saving ? translate("saving") : tCommon("buttons.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================
          DISCARD CHANGES DIALOG
          ============================================================ */}
      <Dialog
        open={discardDialogOpen}
        onClose={() => setDiscardDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            fontFamily: dt.font,
            maxWidth: "380px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "16px",
            fontWeight: 600,
            fontFamily: dt.font,
            color: dt.textPrimary,
            pb: 1,
          }}
        >
          {translate("discardChanges")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontSize: "14px",
              fontFamily: dt.font,
              color: dt.textSecondary,
            }}
          >
            {translate("discardChangesText")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: "8px 20px 16px" }}>
          <Button
            onClick={() => setDiscardDialogOpen(false)}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              fontSize: "13px",
              fontFamily: dt.font,
              color: dt.textSecondary,
              "&:hover": { backgroundColor: dt.inputBg },
            }}
          >
            {translate("keepEditing")}
          </Button>
          <Button
            onClick={handleDiscardConfirm}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "13px",
              fontFamily: dt.font,
              backgroundColor: "#E74C3C",
              color: "#FFFFFF",
              borderRadius: dt.radiusSm,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#C0392B",
                boxShadow: "none",
              },
            }}
          >
            {translate("discard")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MachineEditDialog;
